import type { FeatureFlagsConfig } from './types';

/**
 * Injection seam for the feature-flags door (G1/F5: packages never read
 * `process.env` — app wiring reads `NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS`
 * once in `src/utils/runtime-wiring.ts` and passes the resolved boolean in).
 *
 * SAFE DEFAULT (the P1 logger pattern): module-scope callers may read flags
 * before the app bootstrap has configured anything — pre-configure reads
 * behave as unforced (`forceEnableAll: false`) and never throw.
 *
 * Scope note: this choke point governs the DOOR's read paths only
 * (`readFlag` / `useBooleanFlag`). The ~10 app-side readers of the env knob
 * keep their own `process.env` reads legally (app code is the designated env
 * zone) and converge here only as they migrate onto door reads. The gate
 * family (`getFlagGate` / `resolveFlagGate`) deliberately does NOT consult
 * this override — see read.ts.
 */
const SAFE_DEFAULT: FeatureFlagsConfig = { forceEnableAll: false };

let current: FeatureFlagsConfig = SAFE_DEFAULT;

/**
 * One-time app-bootstrap call (src/utils/runtime-wiring.ts). Takes NO
 * environment input — environment-dependent flag logic is declaration policy
 * and stays app-side (design D4).
 */
export function configureFeatureFlags(config: { forceEnableAll?: boolean }): void {
  current = { forceEnableAll: config.forceEnableAll ?? false };
}

export function getFeatureFlagsConfig(): FeatureFlagsConfig {
  return current;
}

export function __resetFeatureFlagsForTests(): void {
  current = SAFE_DEFAULT;
}
